import { Body, Spacer } from '@spritz-finance/ui'
import React, { useEffect } from 'react'
import { Location, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { FadePresence } from '../../components/FadePresence'
import { InfoBanner } from '../../components/InfoBanner/InfoBanner'
import { CenteredPageInner, PageContainer } from '../../components/Layout/PageContainer'
import { PageLoading } from '../../components/PageLoading'
import { PageTitle } from '../../components/PageTitle'
import { TabBar } from '../../components/TabBar/TabBar'
import { Feature, useFeatureFlag } from '../../context/useFeatureFlag'
import { PaymentProvider } from '../../graphql/generated/graphql'
import { Bills } from './components/bills/Bills'
import { PaymentHistory } from './components/paymentsInProgress/PaymentHistory'
import { PaymentsInProgress } from './components/paymentsInProgress/PaymentsInProgress'

import { useCurrentUser } from '../../context/useCurrentUser'
import { EmbeddedVerificationModule } from '../verification/EmbeddedVerificationModule'
import { useIdentityVerification } from '../verification/useIdentityVerification'
import { VerificationStatus } from '../verification/VerificationContext'

export const FROM_QUESTIONS_PARAM = 'fromQuestions'
export interface IRoute {
    text: string
    path: string
    mobileText?: string
}

const routes: IRoute[] = [
    {
        text: 'All accounts',
        path: 'main',
    },
    {
        text: 'In progress',
        path: 'upcoming',
    },
    {
        text: 'Completed',
        path: 'history',
    },
]

interface BillSubRoutesProps {
    location: Location
}

const BillsSubRoutes: React.VFC<BillSubRoutesProps> = ({ location }) => {
    return (
        <Routes location={location}>
            <Route path="main" element={<Bills />} />
            <Route
                path="upcoming"
                element={
                    <PaymentsInProgress provider={PaymentProvider.MethodFi} paymentType="regular" />
                }
            />
            <Route
                path="history"
                element={
                    <PaymentHistory provider={PaymentProvider.MethodFi} paymentType="regular" />
                }
            />
        </Routes>
    )
}

export const BillsPage: React.VFC = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { pathname } = useLocation()
    const { me } = useCurrentUser()
    const { verificationStatus, loading } = useIdentityVerification()
    const billPayEnabled = useFeatureFlag(Feature.BillPay)
    const subRoute = pathname.split('/')[2]

    const usageAllowed = verificationStatus == VerificationStatus.Verified

    useEffect(() => {
        if (loading) return

        if (!subRoute) {
            navigate(routes[0].path)
            return
        }
    }, [pathname, loading, subRoute, navigate])

    if (loading) return <PageLoading />

    const welcomeText = 'Welcome' + (me.firstName ? `, ${me.firstName}` : '') + '!'

    return (
        <PageContainer>
            <PageTitle subtitle="Add bills, make payments, and track your transactions below">
                {welcomeText}
            </PageTitle>
            {usageAllowed && <TabBar routes={routes} subRoute={subRoute} />}
            <Spacer height={32} />
            {!billPayEnabled && (
                <>
                    <InfoBanner>
                        <Body.Med size={12}>
                            Bill Pay is currently down for maintenance. We are working hard to get
                            it back up as soon as possible. For urgent queries please use the in-app
                            chat or reach out to us at support@spritz.finance
                        </Body.Med>
                    </InfoBanner>
                    <Spacer height={24} />
                </>
            )}
            <CenteredPageInner $width={698}>
                <FadePresence renderKey={location.pathname}>
                    {usageAllowed && <BillsSubRoutes location={location} />}
                    {!usageAllowed && <EmbeddedVerificationModule />}
                </FadePresence>
            </CenteredPageInner>
        </PageContainer>
    )
}
